<template>
  <div :class="['container', $style.container, { [$style.cloudSpacing]: isCloudTab }]">
    <div :class="[$style.wrapper, $style.withScroll]">
      <div :class="[$style.priceTable, $style.withScrollTable]">
        <div :class="$style.headerRow">
          <div :class="[$style.featureCell, $style.title]">Features</div>
          <div 
            v-for="(plan, colIdx) in plans" 
            :key="plan._id" 
            :class="[$style.planCell, { [$style.hovered]: hoveredCol === colIdx }]"
            @mouseenter="hoveredCol = colIdx" 
            @mouseleave="hoveredCol = null"
          >
            <div :class="$style.planName">{{ plan.name }}</div>
            <div :class="$style.planPrice">
              <template v-if="plan.isFree">{{ t('prices.list.period.free') }}</template>
              <template v-else>${{ plan.price.amount }}{{ t('prices.delimetr.month') }}</template>
            </div>
          </div>
        </div>
        <div v-for="feature in featuresList" :key="feature.key" :class="$style.featureRow">
          <div :class="$style.featureCell">{{ feature.label }}</div>
          <div 
            v-for="(plan, colIdx) in plans" 
            :key="plan._id" 
            :class="[$style.planCell, { [$style.hovered]: hoveredCol === colIdx }]"
            @mouseenter="hoveredCol = colIdx" 
            @mouseleave="hoveredCol = null"
          >
            <template v-if="feature.key === 'usageRate'">
              {{ t('prices.list.usageRate') }}
            </template>
            <template 
              v-else-if="typeof plan.features[feature.key] === 'boolean'"
            >
              <span 
                v-if="plan.features[feature.key]"
              >
                <UiIconsCheckCircle />
              </span>
            </template>
            <template v-else>
              {{ plan.features[feature.key] || '' }}
            </template>
          </div>
        </div>
        <div :class="$style.footerRow">
          <div :class="$style.featureCell"></div>
          <div 
            v-for="(plan, colIdx) in plans" 
            :key="plan._id" 
            :class="[$style.planCell, { [$style.hovered]: hoveredCol === colIdx }]"
            @mouseenter="hoveredCol = colIdx" 
            @mouseleave="hoveredCol = null"
          >
            <UiButton 
              :class="$style.action"
              type="smallest"
              color="outline"
              @click.prevent="emits('action', plan._id, true)"
            >
              <template #label>
                {{ !props.user ? t('prices.buttons.create') : t('prices.buttons.plan')}}
              </template>
            </UiButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, type Ref } from 'vue';
import type { PlanItemDto } from '~/dto';
import type { SubscrbPriceTableParams } from './index.js'
import { getCloudPlanFeatures, isCloudPlan } from '~/utils/planFeatures';

const emits = defineEmits(['action']);
const props = defineProps<SubscrbPriceTableParams>()

const { t, te } = useI18n();

const plansRef = inject<Ref<PlanItemDto[] | null> | null>('plans');
const plans = computed(() => {
  const value = plansRef?.value;
  return Array.isArray(value) ? value : [];
});

const hoveredCol = ref<number | null>(null);

const isCloudTab = computed(() => plans.value.some(plan => isCloudPlan(plan)));

const featuresList = computed(() => {
  if (isCloudTab.value) {
    const keys = new Set<string>();
    plans.value.forEach((plan) => {
      getCloudPlanFeatures(plan.features as Record<string, unknown>).forEach(([key]) => {
        keys.add(key);
      });
    });

    return [
      ...Array.from(keys).map((key) => ({
        key,
        label: te(`subscription.params.${key}`) ? t(`subscription.params.${key}`) : key,
      })),
      {
        key: 'usageRate',
        label: t('prices.params.usageRate'),
      },
    ];
  }

  return [
    { key: 'profiles', label: t('prices.params.profiles') },
    { key: 'proxies', label: t('prices.params.proxy') },
    { key: 'patterns', label: t('prices.params.patterns') },
    { key: 'dragAndDropCookies', label: t('prices.params.dnd') },
    { key: 'teamMembers', label: t('prices.params.teamMembers') },
    { key: 'apiAccess', label: t('prices.params.api') },
    { key: 'profilesTransfer', label: t('prices.params.transfer') },
  ];
});
</script>

<style lang="scss" module>
  .container {
    &.cloudSpacing {
      margin-top: 28px;
    }
  }

  .wrapper {
    margin-bottom: 48px;
    
    &.withScroll {
      overflow-x: auto;
      overflow-y: hidden;
      -webkit-overflow-scrolling: touch;
      scroll-behavior: smooth;
      padding-bottom: 8px;
      
      // Кастомный скроллбар для WebKit (Chrome, Safari, Edge)
      &::-webkit-scrollbar {
        height: 8px;
      }
      
      &::-webkit-scrollbar-track {
        background: #f5f5f5;
        border-radius: 4px;
      }
      
      &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 4px;
        transition: background 0.2s ease;
        
        &:hover {
          background: #a8a8a8;
        }
      }
      
      // Кастомный скроллбар для Firefox
      scrollbar-width: thin;
      scrollbar-color: #c1c1c1 #f5f5f5;
    }
  }
  .priceTable {
    display: flex;
    flex-direction: column;
    border-collapse: collapse;
    width: 100%;
    font-size: 15px;
    min-width: fit-content;
  }
  .headerRow {
    border-bottom: 1px solid #292D32;
  }
  .headerRow, .featureRow, .footerRow {
    display: flex;
  }
  .featureCell {
    flex: 0 0 180px;
    padding: 12px 8px;
    border-bottom: 1px solid #EAEAEA;

    &.title {
      color: #121E29;
      font-weight: 700;
    }
  }
  .planCell {
    flex: 1 1 0;
    min-width: 135px;
    text-align: center;
    padding: 12px 8px;
    border-bottom: 1px solid #EAEAEA;
    
    svg {
      width: 16px;
      height: 16px;
    }

    &.hovered {
      background: rgba(247, 247, 247, 0.60);
    }
  }
  .planName {
    color: #121E29;
    font-weight: 700;
  }
  .planPrice {
    color: #888;
    font-size: 13px;
  }
  .regBtn {
    margin: 8px 0;
    padding: 6px 18px;
    border: 1px solid #3b5cff;
    background: #fff;
    color: #3b5cff;
    border-radius: 20px;
    cursor: pointer;
    transition: background 0.2s;
    &:hover {
      background: #3b5cff;
      color: #fff;
    }
  }
</style>